import React from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import validateDNI from '~/utils/validateDNI';
import { registerRequest } from '~/store/modules/auth/actions';

export default function FormConfig({
  children,
  selectedOption,
  setMediaTypeError,
  captchaValue,
  setCaptchaError,
  setCaptchaValue,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  /**
   * La propiedad "policy" se ha cambiado por "enableConsent"
   * el endpoint no espera una propiedad profile, hay que revisar
   * que las propiedades que se envian están desactualizadas o hablar con el
   * back para ver que está pasando.
   */
  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        document: '',
        email: '',
        mediaName: '',
        mediaType: '',
        phoneNumber: '',
        plainPassword: '',
        passwordConfirmation: '',
        enabledConsent: false,
        profile: false,
      }}
      onSubmit={(values, { setSubmitting }) => {
        const selectedOptionIsNotEmpty =
          Object.keys(selectedOption).length !== 0;
        const captchaWasFilled = captchaValue !== null;
        const otherOptionIsSelected = selectedOption.value === 'other';
        const mediaTypeDescriptionIsInvalid =
          values.mediaType === '' || values.mediaType.length < 2;

        if (
          captchaWasFilled &&
          selectedOptionIsNotEmpty &&
          !(otherOptionIsSelected && mediaTypeDescriptionIsInvalid)
        ) {
          // Form data will be sent here if everything is valid
          dispatch(
            registerRequest({
              ...values,
              mediaType:
                selectedOption.value === 'other'
                  ? values.mediaType
                  : selectedOption.value,
              'g-recaptcha-response': captchaValue,
            })
          );
          setSubmitting(false);
          setCaptchaValue(null);
          window.grecaptcha.reset();
          return;
        }

        if (
          selectedOptionIsNotEmpty ||
          (otherOptionIsSelected && mediaTypeDescriptionIsInvalid)
        ) {
          setMediaTypeError(t('mediaTypeIsRequiredValidationError'));
        }

        if (!captchaWasFilled) {
          setCaptchaError(t('captchaIsRequiredValidationError'));
        }

        setSubmitting(false);
        window.grecaptcha.reset();
      }}
      validationSchema={Yup.object().shape({
        firstName: Yup.string()
          .min(3, t('mustHave3OrMoreLetters'))
          .max(30, t('mustHave30OrLessLetters'))
          .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/, t('mustContainOnlyLetters'))
          .required(t('nameIsRequiredValidationError')),
        lastName: Yup.string()
          .min(3, t('mustHave3OrMoreLetters'))
          .max(30, t('mustHave30OrLessLetters'))
          .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/, t('mustContainOnlyLetters'))
          .required(t('lastNameIsRequiredValidationError')),
        document: Yup.string()
          .test('dni-test', t('documentFormatInvalid'), value => {
            return validateDNI(value);
          })
          .required(t('dniIsRequiredValidationError')),
        email: Yup.string()
          .email(t('invalidEmail'))
          .required(t('emailIsRequiredValidationError')),
        mediaName: Yup.string()
          .min(2, t('mustHave3OrMoreLetters'))
          .max(15, t('mustHave15OrLessLetters'))
          .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/, t('mustContainOnlyLetters'))
          .required(t('mediaNameIsRequiredValidationError')),
        mediaType: Yup.string()
          .min(2, t('mustHave2OrMoreLetters'))
          .max(50, t('mustHave50OrLessLetters'))
          .matches(/^[a-zA-Z \s]*$/, t('mustContainOnlyLetters')),
        phoneNumber: Yup.number().required(
          t('telephoneIsRequiredValidationError')
        ),
        plainPassword: Yup.string()
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[.+!@#$%^&*])(?=.{8,20})/,
            t('passwordFormatValidationError')
          )
          .required(t('passwordIsRequiredValidationError')),
        passwordConfirmation: Yup.string()
          .oneOf([Yup.ref('plainPassword'), null], t('passwordsMustMatch'))
          .required(t('confirmPasswordIsRequiredValidationError')),
        profile: Yup.boolean(),
        enabledConsent: Yup.boolean()
          .required(t('policyIsRequiredValidationError'))
          .oneOf([true], t('policyIsRequiredValidationError')),
      })}
    >
      {children}
    </Formik>
  );
}

FormConfig.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  selectedOption: PropTypes.object,
  setMediaTypeError: PropTypes.func.isRequired,
  captchaValue: PropTypes.string,
  setCaptchaValue: PropTypes.func,
  setCaptchaError: PropTypes.func,
};

FormConfig.defaultProps = {
  captchaValue: null,
  setCaptchaValue: () => {},
  setCaptchaError: () => {},
  selectedOption: null,
};
